import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Typography,
  theme,
  Badge,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  LineChartOutlined,
  BulbOutlined,
  CrownOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  NotificationOutlined,
  TrophyOutlined,
  IdcardOutlined,
  KeyOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { token } = theme.useToken();

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '控制台',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
    },
    {
      key: '/login-logs',
      icon: <LoginOutlined />,
      label: '客户登录记录',
    },
    // 视频/文档管理 — 暂时隐藏，恢复时取消注释并设 SHOW_VIDEOS_ADMIN / SHOW_DOCUMENTS_ADMIN = true
    // {
    //   key: '/videos',
    //   icon: <VideoCameraOutlined />,
    //   label: '视频管理',
    // },
    // {
    //   key: '/documents',
    //   icon: <FileTextOutlined />,
    //   label: '文档管理',
    // },
    {
      key: '/trades',
      icon: <LineChartOutlined />,
      label: '首页交易记录配置',
    },
    {
      key: '/strategies',
      icon: <BulbOutlined />,
      label: '首页交易员策略管理',
    },
    {
      key: '/traders',
      icon: <TrophyOutlined />,
      label: '交易员排行榜',
    },
    {
      key: '/profile-config',
      icon: <IdcardOutlined />,
      label: '首页交易员文档配置',
    },
    {
      key: '/vip',
      icon: <CrownOutlined />,
      label: 'VIP管理',
    },
    {
      key: '/activation-codes',
      icon: <KeyOutlined />,
      label: 'VIP激活码管理',
    },
    {
      key: '/vip-strategy',
      icon: <CrownOutlined />,
      label: 'VIP策略配置',
    },
    {
      key: '/vip-signals',
      icon: <LineChartOutlined />,
      label: 'VIP交易信号',
    },
    {
      key: '/popup',
      icon: <NotificationOutlined />,
      label: '弹窗配置',
    },
    {
      key: '/promo',
      icon: <PlayCircleOutlined />,
      label: '首页企业宣传',
    },
    // 股票知识问答 — 暂时隐藏，恢复时取消注释并设 SHOW_QUIZ_ADMIN = true
    // {
    //   key: '/quiz',
    //   icon: <QuestionCircleOutlined />,
    //   label: '股票知识问答',
    // },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Text
            strong
            style={{
              fontSize: collapsed ? 16 : 18,
              color: token.colorPrimary,
              transition: 'all 0.2s',
            }}
          >
            {collapsed ? 'GSC' : 'Gary Strategy Club'}
          </Text>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ border: 'none' }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'all 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              style: { fontSize: 18, cursor: 'pointer' },
              onClick: () => setCollapsed(!collapsed),
            })}
            <Text type="secondary" style={{ marginLeft: 16 }}>
              管理后台
            </Text>
          </div>
          <Space size={24}>
            <Badge count={5} size="small">
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: token.colorPrimary }}
                />
                <Text>{user?.username || 'Admin'}</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          style={{
            margin: 24,
            padding: 24,
            background: token.colorBgContainer,
            borderRadius: token.borderRadius,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;

